/**
 * Сборка сообщений для подписи кошельком. Mini App и бэкенд собирают транзакции
 * только через этот модуль: раскладка полей здесь совпадает с объявлением
 * сообщений в Tact, поэтому контракт читает payload без догадок.
 */
import { Address, Cell, beginCell, toNano as coreToNano } from '@ton/core';

import { JettonOp, Op } from './ops';
import { assertConfig, type CurveConfig } from './config';

/** Сколько TON нужно оставить на исполнение цепочки сообщений сделки. */
export const GAS_BUY = coreToNano('0.12');
export const GAS_SELL = coreToNano('0.15');
export const GAS_CREATE_LAUNCH = coreToNano('0.5');
export const GAS_CLAIM = coreToNano('0.05');

export interface CreateLaunchArgs {
  queryId?: bigint;
  /** Соль запуска: адрес кривой детерминированно считается от (фабрика, деплоер, соль). */
  salt: bigint;
  /** TEP-64 метаданные жетона. */
  content: Cell;
  /** Кошелёк, который забирает долю канала из `FeeVault`. */
  channel: Address;
  seedTon: bigint;
  devBuyTon: bigint;
  /**
   * Сием под адаптер DEX: кривая не знает, какой это DEX. `dexKind` выбирает
   * адаптер, `dexParams` — непрозрачная для кривой ячейка с адресами и
   * шаблонами сообщений конкретного DEX.
   */
  dexKind?: number;
  dexParams?: Cell | null;
}

export function buildCreateLaunchBody(args: CreateLaunchArgs): Cell {
  return beginCell()
    .storeUint(Op.createLaunch, 32)
    .storeUint(args.queryId ?? 0n, 64)
    .storeUint(args.salt, 64)
    .storeRef(args.content)
    .storeAddress(args.channel)
    .storeCoins(args.seedTon)
    .storeCoins(args.devBuyTon)
    .storeUint(args.dexKind ?? 0, 8)
    .storeMaybeRef(args.dexParams ?? null)
    .endCell();
}

/** Сколько TON приложить к `op::create_launch`: плата, сид, dev-buy и газ. */
export function createLaunchValue(args: {
  launchFee: bigint;
  seedTon: bigint;
  devBuyTon: bigint;
  gas?: bigint;
}): bigint {
  return args.launchFee + args.seedTon + args.devBuyTon + (args.gas ?? GAS_CREATE_LAUNCH);
}

export interface BuyArgs {
  queryId?: bigint;
  minOut: bigint;
  /** Unix-время, после которого сообщение исполнять нельзя. */
  deadline: number;
  referrer?: Address | null;
}

export function buildBuyBody(args: BuyArgs): Cell {
  return beginCell()
    .storeUint(Op.buy, 32)
    .storeUint(args.queryId ?? 0n, 64)
    .storeCoins(args.minOut)
    .storeUint(args.deadline, 32)
    .storeAddress(args.referrer ?? null)
    .endCell();
}

/** Сколько TON приложить к `op::buy`: сумма сделки плюс газ. */
export function buyValue(tonIn: bigint, gas: bigint = GAS_BUY): bigint {
  return tonIn + gas;
}

export interface SellArgs {
  queryId?: bigint;
  jettons: bigint;
  minOut: bigint;
  deadline: number;
  referrer?: Address | null;
  /** Адрес кривой: жетоны переводятся на её кошелёк. */
  curve: Address;
  /** Куда вернуть остаток газа. */
  responseDestination: Address;
  /** TON, которые кошелёк кривой перешлёт кривой вместе с уведомлением. */
  forwardTonAmount?: bigint;
}

/**
 * Продажа — это перевод жетонов по TEP-74 на кошелёк кривой с `forward_payload`.
 * Payload не влезает в тело перевода рядом с двумя адресами, поэтому уходит
 * ссылкой: `Either Cell ^Cell` с установленным битом.
 */
export function buildSellPayload(
  args: Pick<SellArgs, 'queryId' | 'minOut' | 'deadline' | 'referrer'>,
): Cell {
  return beginCell()
    .storeUint(Op.sell, 32)
    .storeUint(args.queryId ?? 0n, 64)
    .storeCoins(args.minOut)
    .storeUint(args.deadline, 32)
    .storeAddress(args.referrer ?? null)
    .endCell();
}

export function buildSellBody(args: SellArgs): Cell {
  return beginCell()
    .storeUint(JettonOp.transfer, 32)
    .storeUint(args.queryId ?? 0n, 64)
    .storeCoins(args.jettons)
    .storeAddress(args.curve)
    .storeAddress(args.responseDestination)
    .storeMaybeRef(null)
    .storeCoins(args.forwardTonAmount ?? coreToNano('0.08'))
    .storeBit(true)
    .storeRef(buildSellPayload(args))
    .endCell();
}

export function buildClaimBody(queryId: bigint = 0n): Cell {
  return beginCell().storeUint(Op.claim, 32).storeUint(queryId, 64).endCell();
}

export function buildRebindChannelBody(newRecipient: Address, queryId: bigint = 0n): Cell {
  return beginCell()
    .storeUint(Op.rebindChannel, 32)
    .storeUint(queryId, 64)
    .storeAddress(newRecipient)
    .endCell();
}

export function buildSetProtocolBody(protocol: Address, queryId: bigint = 0n): Cell {
  return beginCell()
    .storeUint(Op.setProtocol, 32)
    .storeUint(queryId, 64)
    .storeAddress(protocol)
    .endCell();
}

/** Tact CurveParams cell, identical to storeCurveParams in the generated contract wrapper. */
export function buildCurveParamsCell(config: CurveConfig, fieldCount: 13 | 20 = 20): Cell {
  assertConfig(config);
  const b = beginCell();
  for (const value of [config.totalSupply, config.curveSupply, config.virtualTon,
    config.virtualJettons, config.graduationThreshold]) b.storeCoins(value);
  for (const value of [config.tradeFeeBps, config.graduationFeeBps, config.antiSnipeMaxBps]) b.storeUint(value, 16);
  b.storeUint(config.antiSnipeWindowSeconds, 32);
  for (const value of [config.devBuyMaxBps, config.feeSplit.channelBps,
    config.feeSplit.protocolBps, config.feeSplit.referrerBps]) b.storeUint(value, 16);
  if (fieldCount === 20) {
    for (const value of [config.routerFeeBps, config.routerSplit.channelBps,
      config.routerSplit.protocolBps, config.routerSplit.referrerBps,
      config.harvestSplit.channelBps, config.harvestSplit.protocolBps,
      config.harvestSplit.referrerBps]) b.storeUint(value, 16);
  }
  return b.endCell();
}

export function buildSetConfigBody(args: {
  config: CurveConfig; treasury: Address; feeVault: Address; launchFee: bigint; queryId?: bigint;
  fieldCount?: 13 | 20;
}): Cell {
  if (args.launchFee < 0n) throw new Error('launchFee must be non-negative');
  return beginCell().storeUint(Op.setConfig, 32).storeUint(args.queryId ?? 0n, 64)
    .storeRef(buildCurveParamsCell(args.config, args.fieldCount)).storeAddress(args.treasury)
    .storeAddress(args.feeVault).storeCoins(args.launchFee).endCell();
}

export function buildPauseLaunchesBody(paused: boolean, queryId: bigint = 0n): Cell {
  return beginCell().storeUint(Op.pauseLaunches, 32).storeUint(queryId, 64).storeBit(paused).endCell();
}

export function buildGraduateBody(queryId: bigint = 0n): Cell {
  return beginCell().storeUint(Op.graduate, 32).storeUint(queryId, 64).endCell();
}

export function buildSeedPoolBody(queryId: bigint = 0n): Cell {
  return beginCell().storeUint(Op.seedPool, 32).storeUint(queryId, 64).endCell();
}

export function buildHarvestLpBody(queryId: bigint = 0n): Cell {
  return beginCell().storeUint(Op.harvestLp, 32).storeUint(queryId, 64).endCell();
}

export const GAS_GRADUATE = coreToNano('0.08');
export const GAS_SEED_POOL = coreToNano('2.5');
export const GAS_HARVEST_LP = coreToNano('1.2');

/** Адреса STON.fi CPI v2, которые клиент кладёт в `dexParams` до подписи запуска. */
export interface StonDexParams {
  router: Address;
  ptonMaster: Address;
  routerPtonWallet: Address;
  routerJettonWallet: Address;
  pool: Address;
  versionMinor: number;
  /** TEP-89 кошелёк LP-жетона пула для кривой: она узнаёт LP только от этого адреса. */
  lpWallet: Address;
}

/**
 * Раскладка `dexParams` для `dexKind = 1`. Три адреса не влезают в одну ячейку
 * вместе с остальными, поэтому вторая тройка уходит ссылкой — это не отклонение
 * от ADR-0008, а единственный способ уложить те же поля.
 */
export function buildStonDexParams(params: StonDexParams): Cell {
  return beginCell()
    .storeAddress(params.router)
    .storeAddress(params.ptonMaster)
    .storeAddress(params.routerPtonWallet)
    .storeRef(
      beginCell()
        .storeAddress(params.routerJettonWallet)
        .storeAddress(params.pool)
        .storeUint(params.versionMinor, 8)
        .storeAddress(params.lpWallet)
        .endCell(),
    )
    .endCell();
}

export function parseStonDexParams(cell: Cell): StonDexParams {
  const s = cell.beginParse();
  const inner = s.loadRef().beginParse();
  return {
    router: s.loadAddress(),
    ptonMaster: s.loadAddress(),
    routerPtonWallet: s.loadAddress(),
    routerJettonWallet: inner.loadAddress(),
    pool: inner.loadAddress(),
    versionMinor: inner.loadUint(8),
    lpWallet: inner.loadAddress(),
  };
}

/** Адреса DeDust volatile v2 — путь отхода для градуации, не для harvest. */
export interface DedustDexParams {
  factory: Address;
  nativeVault: Address;
  jettonVault: Address;
  pool: Address;
  lpWallet: Address;
}

export function buildDedustDexParams(params: DedustDexParams): Cell {
  return beginCell()
    .storeAddress(params.factory)
    .storeAddress(params.nativeVault)
    .storeAddress(params.jettonVault)
    .storeRef(beginCell().storeAddress(params.pool).storeAddress(params.lpWallet).endCell())
    .endCell();
}

export function parseDedustDexParams(cell: Cell): DedustDexParams {
  const s = cell.beginParse();
  const inner = s.loadRef().beginParse();
  return {
    factory: s.loadAddress(),
    nativeVault: s.loadAddress(),
    jettonVault: s.loadAddress(),
    pool: inner.loadAddress(),
    lpWallet: inner.loadAddress(),
  };
}

/** TEP-64 метаданные в off-chain формате: одна ссылка на JSON. */
export function buildOffchainContent(uri: string): Cell {
  return beginCell().storeUint(0x01, 8).storeStringTail(uri).endCell();
}
